import {
  EMPLOYEE_FUNCTION_LABELS,
  EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE,
  INVALID_EMAIL_MESSAGE,
} from "@etudescarre/constants";
import { Button, Drawer, Select, type SelectOption } from "@etudescarre/ui";
import { isValidEmail } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { Referent } from "./work-contracts-editable-panel";

type EmployeeFunction =
  | "directeur_de_travaux"
  | "conduc_de_travaux"
  | "secretariat"
  | "charge_d_affaire"
  | "chef_chantier";

export type WorkContractReferentFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  contractType: "travaux_n_1" | "travaux_n_2";
  performerId: number;
  /** The saved referent, shown on opening. A deactivated one is only replaced. */
  referent?: Referent | null;
  onSuccess?: () => void;
};

export function WorkContractReferentForm({
  open,
  onOpenChange,
  contractId,
  contractType,
  performerId,
  referent,
  onSuccess,
}: WorkContractReferentFormProps) {
  const functionOptions: SelectOption[] = EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE[
    contractType
  ].map((value) => ({
    value,
    label: EMPLOYEE_FUNCTION_LABELS[value] ?? value,
  }));
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [employeeEmailById, setEmployeeEmailById] = useState<
    Record<string, string | null>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [employeeMail, setEmployeeMail] = useState("");
  const [employeeFunction, setEmployeeFunction] =
    useState<EmployeeFunction | null>(null);

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    const { data } = await apiClient.GET("/api/admin/employees", {
      params: { query: { companyId: performerId } },
    });
    if (data) {
      // An employee deactivated in their company cannot be referent.
      const activeEmployees = data.data.employees.filter((e) => e.isActive);
      setEmployeeOptions(
        activeEmployees.map((e) => ({
          label:
            `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() ||
            e.email ||
            "Sans nom",
          value: e.id,
        })),
      );
      setEmployeeEmailById(
        Object.fromEntries(activeEmployees.map((e) => [e.id, e.email])),
      );
    }
    setIsLoading(false);
  }, [performerId]);

  useEffect(() => {
    if (open) {
      const saved = referent?.isActive ? referent : null;
      setEmployeeId(saved?.employeeId ?? null);
      setEmployeeMail(saved?.mail ?? "");
      setEmployeeFunction(
        EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE[contractType].find(
          (f) => f === saved?.function,
        ) ?? null,
      );
      fetchEmployees();
    }
  }, [open, fetchEmployees, referent, contractType]);

  const mail = employeeMail.trim();
  const mailInvalid = mail !== "" && !isValidEmail(mail);

  const onSubmit = async () => {
    if (!employeeId) {
      toaster.error({
        title: "Erreur",
        description: "Veuillez sélectionner un collaborateur",
      });
      return;
    }
    setIsSubmitting(true);
    const { error } = await apiClient.POST(
      "/api/admin/yards/work-contracts/{contractId}/referent",
      {
        params: { path: { contractId } },
        body: {
          employeeId,
          mail: employeeMail.trim() === "" ? null : employeeMail.trim(),
          function: employeeFunction,
        },
      },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'ajout du référent",
      });
      setIsSubmitting(false);
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Référent ajouté avec succès",
    });
    setIsSubmitting(false);
    onSuccess?.();
    onOpenChange(false);
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>Définir le référent</Drawer.Title>
                <Drawer.Description>
                  Sélectionnez un collaborateur de la société contractante
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Collaborateur <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={employeeOptions}
                    value={employeeId ? [employeeId] : []}
                    onValueChange={(details) => {
                      const nextEmployeeId = details.value[0] ?? null;
                      setEmployeeId(nextEmployeeId);
                      setEmployeeMail(
                        nextEmployeeId
                          ? (employeeEmailById[nextEmployeeId] ?? "")
                          : "",
                      );
                      setEmployeeFunction(null);
                    }}
                    placeholder={
                      isLoading
                        ? "Chargement..."
                        : employeeOptions.length === 0
                          ? "Aucun collaborateur pour cette société"
                          : "Sélectionner un collaborateur"
                    }
                    disabled={isLoading || employeeOptions.length === 0}
                  />
                </div>

                {employeeId && (
                  <div>
                    <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Mail de correspondance
                    </span>
                    <input
                      type="email"
                      value={employeeMail}
                      onChange={(e) => setEmployeeMail(e.target.value)}
                      placeholder="Mail de correspondance"
                      aria-invalid={mailInvalid}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    />
                    {mailInvalid && (
                      <p className="mt-1 text-red-600 text-sm">
                        {INVALID_EMAIL_MESSAGE}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Fonction (optionnel)
                  </span>
                  <Select
                    options={functionOptions}
                    value={employeeFunction ? [employeeFunction] : []}
                    onValueChange={(details) =>
                      setEmployeeFunction(
                        (details.value[0] as EmployeeFunction) ?? null,
                      )
                    }
                    placeholder="Sélectionner une fonction"
                  />
                </div>
              </div>

              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={onSubmit}
                  disabled={isSubmitting || !employeeId || mailInvalid}
                >
                  {isSubmitting ? "En cours..." : "Définir"}
                </Button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
